import CancelIcon from "@mui/icons-material/Cancel";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { ISalesUnit } from "Dashboard/scenes/HighRiseSales/domain/ISalesUnit";
import {
  selectState,
  setDeleteSalesUnitModalOpen,
  setSelectedSalesUnit,
} from "../main.slice";
import { selectState as selectStateAuth } from "authentication/main.slice";
import { useSelector } from "react-redux";
import { useAppDispatch } from "hooks/hooks";
import SalesUnitTypeSelect from "Dashboard/scenes/HighRiseSales/components/SalesUnitTypeSelect";
import { ISalesUnitUpdate } from "Dashboard/scenes/HighRiseSales/domain/ISalesUnitUpdate";
import { styles } from "Dashboard/scenes/HighRiseSales/components/SelectStyle";
import { IOption } from "Dashboard/domain/IOption";
import { useLocation, useNavigate } from "react-router-dom";
import InfoTooltip from "components/Popup/InfoTooltip";
import { hasFullAccessTo, hasWriteAccessTo } from "utils/authUtil";
import { HIGH_RISE_SALES_MODULE_NAME } from "Dashboard/scenes/HighRiseSales/domain/HighRiseSalesModule";
import { grey } from "@mui/material/colors";
import NumberInput from "Dashboard/components/NumberInput";
import { FormatNumber } from "utils/numberUtils";
import { INavigationStatePartner } from "authentication/domain/INavigationState";
import SalesUnitStatusSelect from "Dashboard/scenes/Stock/secenes/Input/components/SalesUnitStatusSelect";
import { useReduxField } from "hooks/useReduxField";

interface IProps {
  salesUnit: ISalesUnit;
  index: number;
  salesUnitTypes: IOption[];
  updateSalesUnit: (data: ISalesUnitUpdate) => void;
}

const SalesUnitRow = ({
  salesUnit,
  index,
  salesUnitTypes,
  updateSalesUnit,
}: IProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector(selectStateAuth);
  const { expandedBuilding } = useSelector(selectState);
  const { selectedSalesUnit } = useSelector(selectState);

  const selectSalesUnit = (index: number) => {
    dispatch(setSelectedSalesUnit(index));
  };

  const handlePrice = (priceVal: number) => {
    updateSalesUnit({
      salesUnitId: salesUnit.salesUnitId,
      buildingId: salesUnit.buildingId,
      pricePerSquareMeter: priceVal,
      totalPrice: (salesUnit.squareMeters * priceVal),
    });
  };

  const [pricePerSqM, setPricePerSqM] = useReduxField<number, ISalesUnitUpdate>(
    salesUnit.pricePerSquareMeter,
    (value) => ({
      salesUnitId: salesUnit.salesUnitId,
      buildingId: salesUnit.buildingId,
      pricePerSquareMeter: value,
    }),
    updateSalesUnit
  );

  const [totalPrice, setTotalPrice] = useReduxField<number, ISalesUnitUpdate>(
    salesUnit.totalPrice,
    (value) => ({
      salesUnitId: salesUnit.salesUnitId,
      buildingId: salesUnit.buildingId,
      totalPrice: value,
    }),
    updateSalesUnit
  );

  return (
    <div
      className={`row row-grid-salesunit 
            ${expandedBuilding === salesUnit.buildingId ? "show" : "hide"}
            ${index === selectedSalesUnit ? " active" : ""}`}
      onClick={() => dispatch(setSelectedSalesUnit(index))}
    >
      <div></div>
      <div></div>
      <div className="col strong light">
        <div className="bg-item bg-transparent overflowMinWidth">
          <InfoTooltip tooltip={salesUnit.fullName}
            classesProp="bg-text-overflow">
            {salesUnit.fullName}
          </InfoTooltip>
        </div>
      </div>
      <div className="col center">
        {hasFullAccessTo(user, HIGH_RISE_SALES_MODULE_NAME)
          ? <span onClick={(e) => {
            e.stopPropagation();
            dispatch(
              setDeleteSalesUnitModalOpen({ visible: true, index: index })
            );
          }}
          >
            <CancelIcon
              sx={{
                cursor: "pointer",
                fontSize: 15,
                color: index === selectedSalesUnit ? grey[100] : grey[400],
                '&:hover': { color: "darkred" },
                transition: 'all 0.2s ease-in-out',
              }}
            />
          </span> : <></>}
      </div>
      <div className="col light salesUnitColGap">
        <SalesUnitTypeSelect
          customStyles={styles.customStyles}
          defaultValue={salesUnit.salesUnitType?.typeId}
          options={salesUnitTypes}
          onChange={(salesUnitTypeId: number) => {
            updateSalesUnit({
              salesUnitId: salesUnit.salesUnitId,
              buildingId: salesUnit.buildingId,
              salesUnitTypeId: salesUnitTypeId,
            });
          }}
        />
        <div className="input-flex-basis-55 bg-item bg-item-border-light center">
          {salesUnit.squareMeters}
        </div>
        <NumberInput
          formatAs="quantity"
          className="input-flex-basis-55 bg-item-border-light light center text-medium"
          value={`${pricePerSqM}`}
          onChange={(_: string, __: string, parsed: number) => setPricePerSqM(parsed)}
          disabled={!hasWriteAccessTo(user, HIGH_RISE_SALES_MODULE_NAME)}
        />
        <NumberInput
          formatAs="quantity"
          className="bg-item-border-light center light text-medium"
          value={`${totalPrice}`}
          onChange={(_: string, __: string, parsed: number) => setTotalPrice(parsed)}
          disabled
        />
      </div>
      <div className="col center light">
        <SalesUnitStatusSelect
          customStyles={styles.customStyles}
          defaultValue={salesUnit.salesUnitStatusId}
          onChange={(salesUnitStatusId: number) => {
            updateSalesUnit({
              salesUnitId: salesUnit.salesUnitId,
              buildingId: salesUnit.buildingId,
              salesUnitStatusId: salesUnitStatusId,
            });
          }}
        />
      </div>
      <div className="col light">
        <div className="bg-item bg-item-border center">
          {FormatNumber(salesUnit.receivableAmount)}
        </div>
      </div>
      <div className="col light">
        <div className="bg-item bg-item-border center">
          {FormatNumber(salesUnit.paidAmount)}
        </div>
      </div>
      <div className="col light">
        <div className="bg-item bg-item-border center">
          {FormatNumber(salesUnit.restForPaymant)}
        </div>
      </div>
      <div className="col center">
        {salesUnit.buyer && (
          <span
            className="icon-item"
            onClick={() => {
              navigate("/partners", {
                state: {
                  partnerId: salesUnit.buyer.partnerId,
                  backTo: {
                    pathname: location.pathname,
                    search: location.search,
                    state: location.state,
                  },
                } as INavigationStatePartner,
              });
            }}
          >
            <ArrowForwardIosIcon className="fas svg_icons" />
          </span>
        )}
      </div>
      <div className="col light">
        {salesUnit?.buyer?.fullName ?? ""}
      </div>
      <div></div>
      <div></div>
      <div></div>
      <div></div>
      <div></div>
    </div >
  );
};

export default SalesUnitRow;